import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { custApi } from "../../lib/customerApi";
import { MePageTitle, MeSection, MeStagger } from "./MeUI";
import { useCustomerCms } from "../../lib/useCustomerCms";
import {
  Heartbeat, Stethoscope, TestTube, Truck, FirstAidKit,
  Calendar, ClipboardText, CaretRight, Pill, Wheelchair,
} from "@phosphor-icons/react";

/**
 * /me/health — Health hub
 *   1. Health Services (doctors / labs / ambulance / pharmacy / equipment)
 *   2. Service Booking (upcoming appointments)
 *   3. Booking History (past bookings)
 */
export default function MeHealthLanding() {
  const [bookings, setBookings] = useState({ upcoming: [], past: [] });
  const cms = useCustomerCms();
  const v = (k) => cms.isFeatureVisible("health", k);
  const overlay = (cms.unavailable_overlays || {}).health;
  useEffect(() => {
    custApi.get("/me/health/bookings").then(r => {
      const rows = r.data?.bookings || r.data || [];
      const now = Date.now();
      const upcoming = [], past = [];
      rows.forEach(b => {
        const t = new Date(b.date || b.scheduled_at || b.created_at).getTime();
        (t >= now && b.status !== "cancelled" ? upcoming : past).push(b);
      });
      setBookings({ upcoming, past });
    }).catch(() => {});
  }, []);

  const ALL_SERVICES = [
    { key: "svc_doctors",  to: "/me/health/doctors",     icon: Stethoscope, title: "Doctors",           desc: "Online & in-clinic consults", tone: "red" },
    { key: "svc_labs",     to: "/me/health/labs",        icon: TestTube,    title: "Diagnostic labs",   desc: "Blood tests, home sample pickup", tone: "blue" },
    { key: "svc_ambulance",to: "/me/health/ambulance",   icon: Truck,       title: "Ambulance",         desc: "BLS · ALS · patient transfer", tone: "amber" },
    { key: "svc_pharmacy", to: "/me/services/pharmacy",  icon: Pill,        title: "Pharmacy",          desc: "Medicines delivered", tone: "green" },
    { key: "svc_equipment",to: "/me/services/equipment", icon: Wheelchair,  title: "Equipment rental",  desc: "Wheelchairs, beds, oxygen", tone: "slate" },
    { key: "svc_emergency",to: "/me/health/emergency",   icon: FirstAidKit, title: "Emergency",         desc: "One-tap emergency contacts", tone: "red" },
  ];
  const SERVICES = cms.orderFeatures("health",
    ALL_SERVICES.filter(s => v(s.key)),
    (s) => s.key,
  );

  return (
    <div className="font-body pb-6 relative" data-testid="me-health-landing">
      {/* Content — dimmed + blurred underneath the overlay when enabled */}
      <div className={overlay?.enabled ? "pointer-events-none select-none" : ""}
            aria-hidden={overlay?.enabled ? "true" : undefined}
            style={overlay?.enabled ? { filter: "blur(3px)" } : undefined}>
        <MePageTitle
          eyebrow="Health"
          title={<>Book care,<br/><span className="text-[#DC2626]">on your terms</span></>}
          subtitle="Doctors, labs, ambulance, pharmacy — track every booking here."
        />

        <MeStagger stagger={40}>
        {/* Section 1 — Services grid */}
        <MeSection className="mt-2">
          <SectionHead icon={Heartbeat} title="Health services" note="Choose a service to book" />
          <div className="grid grid-cols-2 gap-3 mt-3">
            {SERVICES.map(s => (
              <ServiceTile key={s.to} {...s} />
            ))}
          </div>
        </MeSection>

        {/* Section 2 — Upcoming bookings */}
        {v("upcoming") && (
        <MeSection className="mt-6">
          <SectionHead icon={Calendar} title="Service booking" note={`${bookings.upcoming.length} upcoming`} />
          {bookings.upcoming.length === 0 ? (
            <EmptyLine text="No upcoming bookings — pick a service above to book." />
          ) : (
            <div className="mt-3 space-y-2">
              {bookings.upcoming.slice(0, 4).map(b => (
                <BookingRow key={b.id} b={b} />
              ))}
              {bookings.upcoming.length > 4 && (
                <Link to="/me/health/bookings" className="block text-center py-2 text-[12px] text-[#DC2626] font-bold" data-testid="health-view-all-upcoming">
                  View all {bookings.upcoming.length} →
                </Link>
              )}
            </div>
          )}
        </MeSection>
        )}

        {/* Section 3 — History */}
        {v("history") && (
        <MeSection className="mt-6">
          <SectionHead icon={ClipboardText} title="Booking history" note={`${bookings.past.length} past`} />
          {bookings.past.length === 0 ? (
            <EmptyLine text="Once you complete a booking, it will show up here." />
          ) : (
            <div className="mt-3 space-y-2">
              {bookings.past.slice(0, 5).map(b => (
                <BookingRow key={b.id} b={b} muted />
              ))}
              {bookings.past.length > 5 && (
                <Link to="/me/health/bookings" className="block text-center py-2 text-[12px] text-[#DC2626] font-bold" data-testid="health-view-all-past">
                  View all history →
                </Link>
              )}
            </div>
          )}
        </MeSection>
        )}
      </MeStagger>
      </div>

      {overlay?.enabled && (
        <UnavailableOverlay title={overlay.title} subtitle={overlay.subtitle}/>
      )}
    </div>
  );
}

// ── Glassy "not available in your location" overlay ─────────────
function UnavailableOverlay({ title, subtitle }) {
  return (
    <div
      data-testid="health-unavailable-overlay"
      className="absolute inset-0 z-30 flex items-center justify-center px-6 py-10"
      style={{
        background: "rgba(255, 251, 246, 0.55)",
        backdropFilter: "blur(4px) saturate(140%)",
        WebkitBackdropFilter: "blur(4px) saturate(140%)",
      }}
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-sm w-full text-center rounded-2xl border border-[#F1DDBB] bg-white/85 backdrop-blur-lg p-6 shadow-[0_20px_50px_rgba(180,83,9,0.18)]">
        <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-[#FDE68A] to-[#F59E0B] text-white flex items-center justify-center shadow-md">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">
            <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm.9 5.2l-.35 8.6h-1.1L11.1 7.2zm-.9 11.35a1.15 1.15 0 110-2.3 1.15 1.15 0 010 2.3z"/>
          </svg>
        </div>
        <div className="mt-4 font-heading text-[17px] text-[#7A2E18] leading-snug"
              data-testid="health-unavailable-title">
          {title || "This feature is currently not available in your location"}
        </div>
        {subtitle && (
          <div className="mt-2 text-[13px] text-[#5C5A55] leading-relaxed"
                data-testid="health-unavailable-subtitle">
            {subtitle}
          </div>
        )}
        <div className="mt-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-bold text-[#B45309] bg-[#FFF7E0] rounded-full px-2.5 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-[#B45309] animate-pulse"/>
          Rolling out soon
        </div>
      </div>
    </div>
  );
}

// ── locally shared bits ────────────────────────────────────
const SectionHead = ({ icon: Icon, title, note }) => (
  <div className="flex items-center gap-2">
    <div className="w-9 h-9 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
      <Icon size={18} weight="fill"/>
    </div>
    <div className="min-w-0">
      <div className="font-display font-black text-[16px] text-[#111827] leading-none">{title}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{note}</div>
    </div>
  </div>
);

const TONE_BG = {
  red:   "from-[#DC2626] to-[#B91C1C]",
  blue:  "from-[#1E3A8A] to-[#1E40AF]",
  amber: "from-[#B45309] to-[#92400E]",
  green: "from-[#15803D] to-[#166534]",
  slate: "from-[#111827] to-[#374151]",
};
const ServiceTile = ({ to, icon: Icon, title, desc, tone }) => (
  <Link
    to={to}
    data-testid={`health-svc-${title.toLowerCase().replace(/\s+/g,"-")}`}
    className="relative rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-[0_6px_20px_rgba(0,0,0,0.04)] p-3 active:scale-[0.98] transition"
  >
    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${TONE_BG[tone] || TONE_BG.red} text-white flex items-center justify-center`}>
      <Icon size={20} weight="fill"/>
    </div>
    <div className="mt-3 text-[14px] font-bold text-[#111827] leading-tight">{title}</div>
    <div className="text-[11px] text-gray-500 mt-0.5">{desc}</div>
  </Link>
);

const BookingRow = ({ b, muted }) => {
  const stamp = b.date || b.scheduled_at || b.created_at;
  const when = stamp ? new Date(stamp).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <Link
      to={`/me/health/bookings`}
      className={`flex items-center gap-3 rounded-2xl bg-white border border-gray-100 shadow-[0_4px_14px_rgba(0,0,0,0.03)] px-3 py-3 active:scale-[0.995] transition ${muted ? "opacity-80" : ""}`}
      data-testid={`health-bk-${b.id || b._id}`}
    >
      <div className="w-11 h-11 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
        <Calendar size={20} weight="fill"/>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-bold text-[#111827] leading-tight truncate">
          {b.service_name || b.title || b.provider_name || (b.category ? b.category.replace(/_/g," ") : "Booking")}
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5 truncate">
          {when}{b.status ? ` · ${b.status.replace(/_/g, " ")}` : ""}
        </div>
      </div>
      <CaretRight size={14} className="text-gray-400 shrink-0"/>
    </Link>
  );
};

const EmptyLine = ({ text }) => (
  <div className="mt-3 rounded-xl border border-dashed border-gray-200 bg-[#FAFAF7] p-4 text-center">
    <div className="text-[12px] text-gray-500">{text}</div>
  </div>
);
